import Link from '@components/common/Link/Link'
import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Button from '@components/ui/ButtonV2/Button'
import FormControl from '@components/ui/inputs/FormControl'
import TextInput from '@components/ui/inputs/TextInput'
import {
  customerCreateFormSchema,
  CustomerCreateFormValues,
  useCustomerCreate,
} from '@features/account/api/customer-create'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import { M55SignupFormFragment } from '@lib/umbraco/generated/types.generated'
import { useRouter } from 'next/router'
import { FC, useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { ImSpinner2 } from 'react-icons/im'
import s from './M55SignupForm.module.scss'

type Step1Props = {
  email: string
  onNext: (data: CustomerCreateFormValues) => void
} & M55SignupFormFragment

const SendCodeStep: FC<Step1Props> = ({
  email,
  onNext,
  defaultGroup,
  ...props
}) => {
  const router = useRouter()

  const { addError, loading, customErrors, clearErrors } = useCustomErrors()

  const { mutateAsync, isPending } = useCustomerCreate({})

  const methods = useForm<CustomerCreateFormValues>({
    resolver: zodResolver(customerCreateFormSchema),
    values: {
      email: email,
    },
  })

  const emailField = methods.watch('email')

  const onStep1Submit = methods.handleSubmit(async (data) => {
    try {
      clearErrors()
      const response = await mutateAsync({ data })

      if (response.customerRegister.errors?.length) {
        addError({
          code: 'customerCreate',
          message: 'An error occurred while creating the customer.',
        })
      } else {
        onNext({ email: data.email })
        // Push sign_up event to GTM dataLayer on account activation
        try {
          if (typeof window !== 'undefined') {
            ;(window as any).dataLayer = (window as any).dataLayer || []
            ;(window as any).dataLayer.push({
              event: 'sign_up',
              method: 'Create profile',
              email: data.email,
            })
          }
        } catch (e) {
          console.warn('Failed to push sign_up event to dataLayer', e)
        }

        // onNext({ email: data.email, groupId: data.groupId })
      }
    } catch (error) {
      addError({
        code: 'customerCreate',
        message: 'An error occurred while creating the customer.',
      })
    }
  })

  // on valid form, clear errors
  useEffect(() => {
    if (methods.formState.isValid) {
      methods.clearErrors()
    }
  }, [methods.formState.isValid])

  return (
    <FormProvider {...methods}>
      <form
        className={s.form}
        onSubmit={onStep1Submit}
        action="#"
        method="post"
      >
        {customErrors && customErrors.length > 0 && (
          <div className={s.errorContainer}>
            {customErrors.map((error) => (
              <div key={error.code} className={s.error}>
                {error.message}
              </div>
            ))}
          </div>
        )}
        <Controller
          control={methods.control}
          name="email"
          render={({ field, fieldState }) => (
            <FormControl required>
              <FormControl.Label>Email</FormControl.Label>
              <TextInput className="w-full" {...field} />
              {fieldState.error && (
                <FormControl.Validation variant="error">
                  {fieldState.error.message}
                </FormControl.Validation>
              )}
            </FormControl>
          )}
        />

        <div className={s.submitWrapper}>
          <Button
            className={s.button}
            variant="secondary"
            type="submit"
            disabled={isPending || loading}
          >
            {isPending || loading ? (
              <ImSpinner2 className={s['icon-spin']} />
            ) : (
              'Continue'
            )}
          </Button>
          <span className={s.secondaryAction}>
            or{' '}
            <Link
              link={{ url: '/account/login' }}
              query={email ? { email: emailField } : undefined}
            >
              login instead
            </Link>
          </span>
        </div>

        <div className={s['paragraph-text']}>
          <TrustedHtml html={props.scTermsText} />
        </div>
      </form>
    </FormProvider>
  )
}

export default SendCodeStep
